import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { ENTITY_TYPE } from '../core/const/entity-type';
import { EntityOption } from '../entity-options-menu/entity-option';
import { FilterMetadata } from '../core/grid/grid-types';
import { PARAM_MATCH_MODE } from '../core/grid/param-match-mode';
import { LastEntityItemChecked } from '../entity-list/base-entity-item/last-entity-item-checked';
import { DIALOG_VISIBILITY } from '../core/const/dialog-visibility';
import { EffectiveRole } from './effective-role';
import { ENTITY_ITEM_ID_KEYS_MAP } from '../core/const/entity-item-id-keys-map';

/**
 * Which stream a selection mutation announces itself on:
 *  - `'change'`  → `change$`, the normal selection-changed path (default)
 *  - `'create'`  → `changeOnCreate$`, for a selection made by creating the entity
 *  - `'none'`    → mutate the store silently; the caller emits later itself
 */
export type SelectionEmit = 'change' | 'create' | 'none';

@Injectable({
  providedIn: 'root'
})
export class ServiceAccessDataService {

  // ── State ─────────────────────────────────────────────────────────────

  private readonly _sharedEntitySelections: {
    [entityType: string]: {
      [idKey: string]: string[]
    }
  } = {
      [ENTITY_TYPE.user]: { connectUserId: [] },
      [ENTITY_TYPE.supportUser]: { connectUserId: [] },
      [ENTITY_TYPE.agent]: { connectUserId: [] },
      [ENTITY_TYPE.userGroup]: { userGroupInvariantKey: [] },
      [ENTITY_TYPE.company]: { tenantId: [] },
      [ENTITY_TYPE.clientCompany]: { tenantId: [] },
      [ENTITY_TYPE.companyGroup]: { companyGroupInvariantKey: [] },
      [ENTITY_TYPE.customer]: { tenantId: [] }
    };

  private _roleSelectionsByContext: {
    [contextKey: string]: {
      [idKey: string]: any[]
    }
  } = {};

  private _pendingIds: string[] = [];

  private _lastEntityItemChecked: LastEntityItemChecked = {
    idKey: '',
    id: ''
  }

  private _connectUserIdsBelongToACustomer: string[] = [];

  private _subjectTypeSelected: string = ENTITY_TYPE.user;
  private _contextTypeSelected: string = ENTITY_TYPE.company;
  private _assignmentTypeSelected: string = ENTITY_TYPE.role;

  private _entityOptionTypeSelected: string = ENTITY_TYPE.none;

  private _currentEntityOption: EntityOption = new EntityOption();
  private _oldEntityOption: EntityOption = new EntityOption();

  private _lastEfectiveRolesChanged: EffectiveRole[] = [];

  // ── Streams ───────────────────────────────────────────────────────────

  private readonly _subjectEntityOption = new Subject<EntityOption>();
  public subjectEntityOption$: Observable<EntityOption> = this._subjectEntityOption.asObservable();

  private readonly _contextEntityOption = new Subject<EntityOption>();
  public contextEntityOption$: Observable<EntityOption> = this._contextEntityOption.asObservable();

  private readonly _assignmentEntityOption = new Subject<EntityOption>();
  public assignmentEntityOption$: Observable<EntityOption> = this._assignmentEntityOption.asObservable();

  private readonly _setFocusOn = new Subject<string>();
  public setFocusOn$: Observable<string> = this._setFocusOn.asObservable();

  private readonly _change = new Subject<{ [key: string]: FilterMetadata }>();
  public change$: Observable<{ [key: string]: FilterMetadata }> = this._change.asObservable();

  private readonly _changeOnCreate = new Subject<{ [key: string]: FilterMetadata }>();
  public changeOnCreate$: Observable<{ [key: string]: FilterMetadata }> = this._changeOnCreate.asObservable();

  private readonly _refresh = new Subject<{ [key: string]: FilterMetadata }>();
  public refresh$: Observable<{ [key: string]: FilterMetadata }> = this._refresh.asObservable();

  private readonly _refreshOnTerminateSubscriber = new Subject<void>();
  public refreshOnTerminateSubscriber$: Observable<void> = this._refreshOnTerminateSubscriber.asObservable();

  private readonly _resetCheckboxes = new Subject<void>();
  public resetCheckboxes$: Observable<void> = this._resetCheckboxes.asObservable();


  // Only ever hidden through here — the dialog is shown by the consumer's own signal.
  private readonly _limitWarningDialog = new Subject<void>();
  public limitWarningDialog$: Observable<void> = this._limitWarningDialog.asObservable();

  private readonly _removeUserFromUserGroupDialog = new Subject<string>();
  public removeUserFromUserGroupDialog$: Observable<string> = this._removeUserFromUserGroupDialog.asObservable();

  private readonly _activateServiceDialog = new Subject<string>();
  public activateServiceDialog$: Observable<string> = this._activateServiceDialog.asObservable();


  private readonly _resetLastEntityItemChecked = new Subject<LastEntityItemChecked>();
  public resetLastEntityItemChecked$: Observable<LastEntityItemChecked> = this._resetLastEntityItemChecked.asObservable();

  private readonly _refreshUsersList = new Subject<void>();
  public refreshUsersList$: Observable<void> = this._refreshUsersList.asObservable();

  private readonly _refreshPendingUsers = new Subject<void>();
  public refreshPendingUsers$: Observable<void> = this._refreshPendingUsers.asObservable();

  private readonly _refreshUsersWithoutAccess = new Subject<void>();
  public refreshUsersWithoutAccess$: Observable<void> = this._refreshUsersWithoutAccess.asObservable();

  private readonly _refreshUsersWithoutAccessOnAddSelected = new Subject<void>();
  public refreshUsersWithoutAccessOnAddSelected$: Observable<void> = this._refreshUsersWithoutAccessOnAddSelected.asObservable();

  // ── Entity option & type selection ────────────────────────────────────

  setEntityOptionSelected(option: EntityOption): void {
    this._entityOptionTypeSelected = option.type;
    this._currentEntityOption = option;
    this._oldEntityOption = option;
  }

  getEntityOptionTypeSelected(): string {
    return this._entityOptionTypeSelected;
  }

  getCurrentEntityOption(): EntityOption {
    return this._currentEntityOption;
  }

  getOldEntityOption(): EntityOption {
    return this._oldEntityOption;
  }

  getSubjectTypeSelected(): string {
    return this._subjectTypeSelected;
  }

  setSubjectTypeSelected(entityType: string): void {
    this._subjectTypeSelected = entityType;
  }

  getContextTypeSelected(): string {
    return this._contextTypeSelected;
  }

  setContextTypeSelected(entityType: string): void {
    this._contextTypeSelected = entityType;
  }

  getAssignmentTypeSelected(): string {
    return this._assignmentTypeSelected;
  }

  setAssignmentTypeSelected(entityType: string): void {
    this._assignmentTypeSelected = entityType;
  }

  getLastEffectiveRolesChanged(): EffectiveRole[] {
    return this._lastEfectiveRolesChanged;
  }

  setLastEffectiveRolesChanged(effectiveRoles: EffectiveRole[]): void {
    this._lastEfectiveRolesChanged = effectiveRoles;
  }

  setSubjectOption(option: EntityOption): void {
    this._subjectTypeSelected = option.type;
    this._subjectEntityOption.next(option);
  }

  setContextOption(option: EntityOption): void {
    this._contextTypeSelected = option.type;
    this._contextEntityOption.next(option);
  }

  setAssignmentOption(option: EntityOption): void {
    this._assignmentTypeSelected = option.type;
    this._assignmentEntityOption.next(option);
  }

  // ── Pending ───────────────────────────────────────────────────────────

  getPendingIds(): string[] {
    return this._pendingIds;
  }

  addToPendingIds(id: string): void {
    this._pendingIds.push(id);
  }

  removeFromPendingIds(id: string): void {
    this._pendingIds = this._pendingIds.filter(value => { return value !== id; });
  }

  isInPendingIds(id: string): boolean {
    return this._pendingIds.includes(id);
  }

  removeFromPendingIdsAndRefreshTheUserList(id: string): void {
    this._pendingIds = this._pendingIds.filter(value => { return value !== id; });
    this._refreshPendingUsers.next();
  }

  // ── belongsTo (users that already belong to a customer) ───────────────

  // A set, not a list: `remove` drops every occurrence and `isIdIn` only asks whether the id is
  // there, so a second push carries no meaning - it just made the id be linked (and reported)
  // twice. Guarding the push is what keeps the other three methods honest.
  addConnectUserIdToBelongsToACustomerArray(connectUserId: string): void {
    if (!this._connectUserIdsBelongToACustomer.includes(connectUserId)) {
      this._connectUserIdsBelongToACustomer.push(connectUserId);
    }
  }

  removeConnectUserIdFromBelongsToACustomerArray(connectUserId: string): void {
    this._connectUserIdsBelongToACustomer = this._connectUserIdsBelongToACustomer.filter(value => value !== connectUserId);
  }

  getConnectUserIdsBelongsToACustomerArray(): string[] {
    return this._connectUserIdsBelongToACustomer;
  }

  isIdInConnectUserIdsBelongsToACustomerArray(connectUserId: string): boolean {
    return this._connectUserIdsBelongToACustomer.includes(connectUserId);
  }

  resetConnectUserIdsBelongsToACustomerArray(): void {
    this._connectUserIdsBelongToACustomer = [];
  }

  // ── Grid row counts ───────────────────────────────────────────────────
  //
  // What the two service-access tables are showing, published by the component that fills
  // them. Read by the assistant's read-only tool, which needs to say how many assignments are
  // waiting for a Grant without reaching into a component it does not own. Plain fields, not
  // signals: nothing renders from them.

  private _currentAccessRowCount = 0;
  private _possibleAccessRowCount = 0;

  setAccessRowCounts(current: number, possible: number): void {
    this._currentAccessRowCount = current;
    this._possibleAccessRowCount = possible;
  }

  getCurrentAccessRowCount(): number {
    return this._currentAccessRowCount;
  }

  getPossibleAccessRowCount(): number {
    return this._possibleAccessRowCount;
  }

  // ── Selection readers ─────────────────────────────────────────────────

  getSelectedItems(idKey: string): any[] {
    const state = this.getCurrentSelectionState();
    return state[idKey] || [];
  }

  getCurrentSelectionState(): { [idKey: string]: any[] } {
    const subject = this._subjectTypeSelected;
    const context = this._contextTypeSelected;
    const roleState = this.getRoleStateForCurrentContext();

    // Merge shared entity selections with role selections for current context
    return {
      ...this._sharedEntitySelections[subject],
      ...this._sharedEntitySelections[context],
      ...roleState
    };
  }

  showLimitWarningDialog(): boolean {
    let has = false;
    let count = 0;
    const obj = this.getCurrentSelectionState();
    const keys = Object.keys(obj);
    for (let i = 0, len = keys.length; i < len; i++) {
      if (obj[keys[i]].length > 0) {
        count += 1;
      }
    }
    if (count >= 3) {
      has = true;
    }
    return has;
  }

  hasThreeSelections(): boolean {
    let selectionCount = 0;
    const state = this.getCurrentSelectionState();
    // Check non-role keys
    if (state['connectUserId'] && state['connectUserId'].length > 0) selectionCount++;
    if (state['tenantId'] && state['tenantId'].length > 0) selectionCount++;
    if (state['userGroupInvariantKey'] && state['userGroupInvariantKey'].length > 0) selectionCount++;
    if (state['companyGroupInvariantKey'] && state['companyGroupInvariantKey'].length > 0) selectionCount++;

    // Check role keys (count as 1 if both have values)
    if (state['roleInvariantKey'] && state['roleInvariantKey'].length > 0) {
      selectionCount++;
    }

    return selectionCount >= 3;
  }

  // ── Selection mutators & emission ─────────────────────────────────────

  clearSelectionsFor(type: string, idKey: string): void {
    this._entityOptionTypeSelected = type;

    if (idKey === 'roleInvariantKey') {
      const roleState = this.getRoleStateForCurrentContext();
      roleState[idKey] = [];
    } else {
      const entityType = this.getEntityTypeFromIdKey(idKey);
      if (entityType && this._sharedEntitySelections[entityType]) {
        this._sharedEntitySelections[entityType][idKey] = [];
      }
    }

    const value = this.getFilters();
    this._refresh.next(value);
  }

  // ── Focus ─────────────────────────────────────────────────────────────

  setFocusOn(type: string): void {
    this._setFocusOn.next(type);
  }

  // ── Dialogs ───────────────────────────────────────────────────────────

  hideRemoveUserFromUserGroupDialog(): void {
    this._removeUserFromUserGroupDialog.next(DIALOG_VISIBILITY.hide);
  }

  hideAndRefreshRemoveUserFromUserGroupDialog(): void {
    this._removeUserFromUserGroupDialog.next(DIALOG_VISIBILITY.hideAndRefresh);
  }

  hideActivateServiceDialog(): void {
    this._activateServiceDialog.next(DIALOG_VISIBILITY.hide);
  }

  hideAndRefreshActivateServiceDialog(): void {
    this._activateServiceDialog.next(DIALOG_VISIBILITY.hideAndRefresh);
  }

  hideLimitWarningDialog(): void {
    this._limitWarningDialog.next();
  }

  change(): void {
    const value = this.getFilters();
    this._change.next(value);
  }

  /**
   * Which change stream a selection mutation announces itself on.
   *
   * `add` / `addOnCreate` / `addWithoutTriggerChange` used to be three near-identical methods that
   * differed ONLY in this, and `remove` / `removeWithoutTriggerChange` likewise — so the choice is
   * now an explicit argument instead of being encoded in the method name. `'none'` is the
   * emission-order control the basket relies on: it mutates the store without waking the
   * service-access grid, for callers that emit later themselves.
   */
  private emitSelectionChange(emit: SelectionEmit): void {
    if (emit === 'change') {
      this._change.next(this.getFilters());
    } else if (emit === 'create') {
      this._changeOnCreate.next(this.getFilters());
    }
  }

  add(idKey: string, id: string, item: any, emit: SelectionEmit = 'change'): void {
    this.addToAppropriateStore(idKey, item);
    this._lastEntityItemChecked = { id: id, idKey: idKey };
    this.emitSelectionChange(emit);
  }

  addOnCreateItems(arr: Array<{ idKey: string; id: string; }>, items: any[]): void {
    for (let i = 0, len = arr.length; i < len; i++) {
      this.addToAppropriateStore(arr[i].idKey, items[i]);
    }
    const last = arr.at(-1);
    if (last) {
      this._lastEntityItemChecked = { id: last.id, idKey: last.idKey };
    }
    this.emitSelectionChange('create');
  }

  // NB the asymmetry with `add`, which is deliberate and pre-existing: every add variant records
  // `_lastEntityItemChecked`, remove does not.
  remove(idKey: string, id: string, emit: SelectionEmit = 'change'): void {
    this.removeFromAppropriateStore(idKey, id);
    this.emitSelectionChange(emit);
  }

  // ── Reset / clear ─────────────────────────────────────────────────────

  resetAndChangeforMain(): void {
    // Reset shared entity selections
    this.resetSharedEntitySelections();

    // Reset role selections for all contexts
    this.resetRoleSelectionsForAllContexts();

    this._entityOptionTypeSelected = ENTITY_TYPE.none;
    const data = this.getFilters();
    this._change.next(data);
  }

  reset(idKey1?: string, id1?: string, idKey2?: string, id2?: string): void {
    // Reset shared entity selections
    this.resetSharedEntitySelections();

    this.resetRoleSelectionsForAllContexts();

    this._entityOptionTypeSelected = ENTITY_TYPE.none;
    this._resetCheckboxes.next();

    if (idKey1 !== undefined && id1 !== undefined) {
      this.addToAppropriateStore(idKey1, { [idKey1]: id1 });
      if (idKey2 !== undefined && id2 !== undefined) {
        this.addToAppropriateStore(idKey2, { [idKey2]: id2 });
      }
      const data = this.getFilters();
      this._change.next(data);
    }
  }

  resetOnTerminateSubscriber(): void {
    this.resetSelectedIdsForMain();
    this._entityOptionTypeSelected = ENTITY_TYPE.none;
    this._subjectTypeSelected = ENTITY_TYPE.user;
    this._contextTypeSelected = ENTITY_TYPE.company;
    this._assignmentTypeSelected = ENTITY_TYPE.role;
    this._refreshOnTerminateSubscriber.next();
  }

  resetSelectedIdsForMain(): void {
    // Reset all shared entity selections
    Object.keys(this._sharedEntitySelections).forEach(entityType => {
      Object.keys(this._sharedEntitySelections[entityType]).forEach(idKey => {
        this._sharedEntitySelections[entityType][idKey] = [];
      });
    });

    // Reset all role selections
    this._roleSelectionsByContext = {};
  }

  resetLastEntityItemChecked(): void {
    const last = this._lastEntityItemChecked;
    const state = this.getCurrentSelectionState();

    if (state[last.idKey]) {
      // The selection stores hold whole items, not ids — compare on the id field,
      // the same way removeFromAppropriateStore does. Optional chaining because the
      // array can contain holes (see the spec).
      const arr = state[last.idKey].filter((item: any) => item?.[last.idKey] === last.id);
      if (arr.length > 0) {
        this.removeFromAppropriateStore(last.idKey, last.id);
        // The item is no longer selected, so its id must not stay in the belongsTo list either -
        // it would have the next "grant all" link a user nobody selected. The deselect path in
        // entity-list already pairs the two; this one is reached by closing the limit dialog.
        if (last.idKey === 'connectUserId') {
          this.removeConnectUserIdFromBelongsToACustomerArray(last.id);
        }
        this._resetLastEntityItemChecked.next(last);
        this._lastEntityItemChecked = { id: '', idKey: '' };
        const value = this.getFilters();
        this._change.next(value);
      }
    }
  }

  // ── Refresh / reload ──────────────────────────────────────────────────
  // Five methods that were spread across five places in this file. Grouped, the difference
  // between them is finally readable: which stream each one wakes.

  refresh(): void {
    this._refresh.next(this.getFilters());
  }

  reloadMainServiceAccess(): void {
    const value = this.getFilters();
    this._change.next(value);
  }

  refreshUserList(): void {
    this._refreshUsersList.next()
  }

  refreshUsersWithoutAccess(): void {
    this._refreshUsersWithoutAccess.next();
  }

  refreshUsersWithoutAccessOnAddSelected(): void {
    this._refreshUsersWithoutAccessOnAddSelected.next();
  }

  // ── Filters (derived from the current selection) ──────────────────────

  getFilters(): { [key: string]: FilterMetadata } {
    const state = this.getCurrentSelectionState();
    const subjectKey = this.getKeyName(this._subjectTypeSelected);
    const contextKey = this.getKeyName(this._contextTypeSelected);
    const assignmentKey = this.getKeyName(this._assignmentTypeSelected);

    const allowedKeys = new Set([subjectKey, contextKey, assignmentKey].filter(key => key !== undefined && key !== ''));

    const filters: { [key: string]: FilterMetadata } = {};
    const map = this.getKeysWithValue(state);
    const keys = Object.keys(map);

    for (let i = 0, len = keys.length; i < len; i++) {
      const key: string = keys[i];
      if (allowedKeys.has(key)) {
        // Extract IDs from items array
        const res = map[key].map((item: any) => item?.[key]).filter((value: any) => value !== undefined);
        filters[key] = { matchMode: PARAM_MATCH_MODE.in, value: `[${res.toString().replaceAll(',', '|')}]` };
      }
    }
    return filters;
  }

  hasFilters(): boolean {
    const state = this.getCurrentSelectionState();
    const map = this.getKeysWithValue(state);
    return Object.keys(map).length > 0;
  }

  // ── Private helpers (stores, keys, id-key mapping) ────────────────────

  private getKeysWithValue(state: { [idKey: string]: any[] }): { [key: string]: any[] } {
    let map: { [key: string]: any[] } = {};
    const keys = Object.keys(state);
    for (let i = 0, len = keys.length; i < len; i++) {
      const arr = state[keys[i]];
      if (arr && arr.length > 0) {
        map[keys[i]] = arr;
      }
    }
    return map;
  }

  private getKeyName(type: string): string {
    return ENTITY_ITEM_ID_KEYS_MAP[`${type.charAt(0).toLowerCase()}${type.slice(1)}`];
  }

  private getContextKey(): string {
    return `${this._subjectTypeSelected}_${this._contextTypeSelected}_${this._assignmentTypeSelected}`;
  }

  // Holds RoleItem objects, not ids — the previous `string[]` annotation was wrong and
  // is what let `resetLastEntityItemChecked` compare items against an id unnoticed.
  private getRoleStateForCurrentContext(): { [idKey: string]: any[] } {
    const contextKey = this.getContextKey();

    if (!this._roleSelectionsByContext[contextKey]) {
      this._roleSelectionsByContext[contextKey] = {
        roleInvariantKey: []
      };
    }

    return this._roleSelectionsByContext[contextKey];
  }

  private addToAppropriateStore(idKey: string, item: any): void {
    if (idKey === 'roleInvariantKey') {
      const roleState = this.getRoleStateForCurrentContext();
      if (!roleState[idKey]) {
        roleState[idKey] = [];
      }
      // Check if item already exists
      const exists = roleState[idKey].some((i: any) => i[idKey] === item[idKey]);
      if (!exists) {
        roleState[idKey].push(item);
      }
    } else {
      // Add to shared entity selections
      const entityType = this.getEntityTypeFromIdKey(idKey);
      if (entityType && this._sharedEntitySelections[entityType]) {
        if (!this._sharedEntitySelections[entityType][idKey]) {
          this._sharedEntitySelections[entityType][idKey] = [];
        }
        // Check if item already exists
        const exists = this._sharedEntitySelections[entityType][idKey].some((i: any) => i[idKey] === item[idKey]);
        if (!exists) {
          this._sharedEntitySelections[entityType][idKey].push(item);
        }
      }
    }
  }


  private removeFromAppropriateStore(idKey: string, id: string): void {
    if (idKey === 'roleInvariantKey') {
      const roleState = this.getRoleStateForCurrentContext();
      if (roleState[idKey]) {
        roleState[idKey] = roleState[idKey].filter((item: any) => item?.[idKey] !== id);
      }
    } else {
      // Remove from shared entity selections
      const entityType = this.getEntityTypeFromIdKey(idKey);
      if (entityType && this._sharedEntitySelections[entityType]?.[idKey]) {
        this._sharedEntitySelections[entityType][idKey] =
          this._sharedEntitySelections[entityType][idKey].filter((item: any) => item?.[idKey] !== id);
      }
    }
  }

  private getEntityTypeFromIdKey(idKey: string): string | undefined {
    const subject = this._subjectTypeSelected;
    const context = this._contextTypeSelected;

    // Check if idKey belongs to subject entity
    if (this._sharedEntitySelections[subject]?.hasOwnProperty(idKey)) {
      return subject;
    }

    // Check if idKey belongs to context entity
    if (this._sharedEntitySelections[context]?.hasOwnProperty(idKey)) {
      return context;
    }

    return undefined;
  }

  private resetSharedEntitySelections(): void {
    Object.keys(this._sharedEntitySelections).forEach(entityType => {
      if (
        this._sharedEntitySelections[entityType] &&
        typeof this._sharedEntitySelections[entityType] === 'object' &&
        !Array.isArray(this._sharedEntitySelections[entityType]) &&
        this._sharedEntitySelections[entityType] !== null
      ) {
        Object.keys(this._sharedEntitySelections[entityType]).forEach(idKey => {
          this._sharedEntitySelections[entityType][idKey] = [];
        });
      }
    });
  }

  private resetRoleSelectionsForAllContexts(): void {
    Object.keys(this._roleSelectionsByContext).forEach(contextKey => {
      const roleState = this._roleSelectionsByContext[contextKey];
      Object.keys(roleState).forEach(key => {
        if (Array.isArray(roleState[key])) {
          roleState[key] = [];
        }
      });
    });
  }

}
